// src/views/WorkflowEditor/WorkflowBuilderView.jsx
// Vista para crear/editar workflows

import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import WorkflowBuilder from '../../components/WorkflowCreator/WorkflowBuilder';
import WorkflowService from '../../services/workflow.service';
import { NODE_DEFINITIONS } from '../../components/WorkflowCreator/nodes/nodeDefinitions';
import { Loader2 } from 'lucide-react';

/**
 * Convierte los datos del workflow del backend a nodos visuales de ReactFlow
 */
const convertWorkflowToNodes = (workflow) => {
  const nodes = [];
  const edges = [];
  
  const triggers = workflow.triggers || [];
  const steps = workflow.steps || [];
  
  // Ordenar steps por step_order para mantener el orden correcto
  const sortedSteps = [...steps].sort((a, b) => {
    const orderA = a.step_order !== undefined ? a.step_order : 999;
    const orderB = b.step_order !== undefined ? b.step_order : 999;
    return orderA - orderB;
  });
  
  // Steps sorted by step_order
  
  // Posición inicial y espaciado
  const startX = 400;
  const startY = 100;
  const nodeSpacingY = 200;  // Aumentado de 180 a 200 para más espacio
  const nodeSpacingX = 300;
  
  // Crear nodos de triggers
  triggers.forEach((trigger, index) => {
    const triggerType = trigger.trigger_type || trigger.configuration?.trigger_type || 'webhook';
    
    // Mapear tipo de trigger a nodeDefinition type
    let mappedType = `${triggerType}_trigger`;
    if (triggerType === 'shopify') mappedType = 'shopify_trigger';
    else if (triggerType === 'webhook') mappedType = 'webhook_trigger';
    else if (triggerType === 'schedule') mappedType = 'schedule_trigger';
    else if (triggerType === 'manual') mappedType = 'manual_trigger';
    else if (triggerType === 'whatsapp') mappedType = 'whatsapp_trigger';
    
    // Obtener la definición del nodo
    const definition = NODE_DEFINITIONS[mappedType] || NODE_DEFINITIONS.webhook_trigger;
    
    const triggerId = trigger.id || `trigger-${index}`;
    
    nodes.push({
      id: triggerId,
      type: mappedType, // Usar el tipo mapeado directamente
      position: { x: startX + (index * nodeSpacingX), y: startY },
      data: {
        ...definition, // Incluir toda la definición (label, icon, color, etc.)
        type: mappedType,
        nodeId: triggerId,
        stepName: trigger.configuration?.description || definition.label || `Trigger ${index + 1}`,
        stepDescription: trigger.configuration?.description || definition.description || '',
        params: {
          ...trigger.configuration,
          event_type: trigger.configuration?.event_type,
          shop_domain: trigger.configuration?.shop_domain,
          integration_id: trigger.configuration?.integration_id
        },
        isConfigured: true
      }
    });
  });
  
  // Crear nodos de steps
  sortedSteps.forEach((step, index) => {
    const stepParams = step.parameters || {};
    const stepType = stepParams.step_type || stepParams.type || 'ai_assistant';
    
    // Processing step
    
    // El step_type del backend debe coincidir con el type de NODE_DEFINITIONS
    // Mapear step_type a NODE_DEFINITIONS type
    let mappedType = stepType;
    
    // Mapeo de step_type del backend a tipo de NODE_DEFINITIONS
    if (stepType === 'ai_assistant' || stepType === 'assistant') {
      mappedType = 'assistant_task';
    }
    // Para otros tipos, el step_type debe coincidir directamente con NODE_DEFINITIONS
    
    // Verificar si el tipo existe en NODE_DEFINITIONS
    const definition = NODE_DEFINITIONS[mappedType] || NODE_DEFINITIONS.assistant_task || {};
    
    const stepId = step.id || `step-${index}`;
    
    nodes.push({
      id: stepId,
      type: mappedType, // Usar el tipo mapeado directamente
      position: { x: startX, y: startY + ((index + 1) * nodeSpacingY) },
      data: {
        ...definition, // Incluir toda la definición
        type: mappedType,
        nodeId: stepId,
        stepName: step.name || definition.label || `Step ${index + 1}`,
        stepDescription: step.description || definition.description || '',
        params: stepParams,
        isConfigured: Object.keys(stepParams).length > 0
      }
    });
    
    // Crear edges basados en step_order y dependencies
    if (step.dependencies && step.dependencies.length > 0) {
      step.dependencies.forEach(depId => {
        edges.push({
          id: `edge-${depId}-${stepId}`,
          source: depId,
          target: stepId,
          sourceHandle: 'bottom',  // Sale desde abajo del nodo anterior
          targetHandle: 'top',      // Entra por arriba del nodo actual
          type: 'smoothstep',
          animated: true
        });
      });
    } else if (index === 0 && triggers.length > 0) {
      // Primer step conectado al primer trigger
      edges.push({
        id: `edge-trigger-${stepId}`,
        source: triggers[0].id || 'trigger-0',
        target: stepId,
        sourceHandle: 'bottom',
        targetHandle: 'top',
        type: 'smoothstep',
        animated: true
      });
    } else if (index > 0) {
      // Conectar al step anterior si no hay dependencias explícitas
      const prevStep = sortedSteps[index - 1];
      const prevStepId = prevStep.id || `step-${index - 1}`;
      edges.push({
        id: `edge-${prevStepId}-${stepId}`,
        source: prevStepId,
        target: stepId,
        sourceHandle: 'bottom',
        targetHandle: 'top',
        type: 'smoothstep',
        animated: true
      });
    }
  });
  
  // Si solo hay trigger y no hay steps, no crear edges adicionales
  
  return { nodes, edges };
};

const WorkflowBuilderView = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Accept both 'id' and 'workflowId' for backwards compatibility
  const workflowId = searchParams.get('id') || searchParams.get('workflowId');
  const projectId = searchParams.get('projectId');
  const [initialData, setInitialData] = useState(null);
  const [isLoading, setIsLoading] = useState(!!workflowId);
  const [error, setError] = useState(null);

  // Load existing workflow if editing
  useEffect(() => {
    const loadWorkflow = async () => {
      if (!workflowId) return;
      
      try {
        setIsLoading(true);
        const result = await WorkflowService.getWorkflow(workflowId);
        
        console.log('Loaded workflow:', result);
        
        if (result.success && result.data) {
          // Convert workflow data to builder format
          const workflow = result.data.workflow || result.data;
          const stepsData = result.data.steps || [];
          const triggersData = result.data.triggers || [];
          
          
          // If we have saved UI state, use it
          if (workflow._ui_state && workflow._ui_state.nodes?.length > 0) {
            // Using saved UI state
            setInitialData({
              name: workflow.name,
              description: workflow.description,
              tags: workflow.tags || [],
              nodes: workflow._ui_state.nodes,
              edges: workflow._ui_state.edges || []
            });
          } else {
            // Reconstruct nodes from triggers and steps
            
            // Usar los steps y triggers completos del backend
            const workflowWithSteps = {
              ...workflow,
              steps: stepsData,
              triggers: triggersData.length > 0 ? triggersData : workflow.triggers || []
            };
            
            const { nodes, edges } = convertWorkflowToNodes(workflowWithSteps);
            
            setInitialData({
              name: workflow.name,
              description: workflow.description,
              tags: workflow.tags || [],
              nodes,
              edges
            });
          }
        } else {
          setError(result.error || 'Failed to load workflow');
        }
      } catch (err) {
        console.error('Error loading workflow:', err);
        setError(err.message || 'Failed to load workflow');
      } finally {
        setIsLoading(false);
      }
    };

    loadWorkflow();
  }, [workflowId]);

  const handleSave = (workflow) => {
    // Workflow saved
    // Navigate back to project workflows tab
    if (projectId) {
      navigate(`/dashboard?view=Projects&projectId=${projectId}&tab=workflows`);
    } else {
      navigate('/dashboard');
    }
  };

  const handleBack = () => {
    // Navigate back to project workflows tab
    if (projectId) {
      navigate(`/dashboard?view=Projects&projectId=${projectId}&tab=workflows`);
    } else {
      navigate('/dashboard');
    }
  };

  if (isLoading) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-[#0a0a0a]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-purple-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading workflow...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-[#0a0a0a]">
        <div className="text-center">
          <p className="text-red-400 mb-4">{error}</p>
          <button
            onClick={handleBack}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <WorkflowBuilder
      workflowId={workflowId}
      projectId={projectId}
      initialData={initialData}
      onSave={handleSave}
      onBack={handleBack}
    />
  );
};

export default WorkflowBuilderView;
